import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Cpu, Zap, HardDrive, Play, Loader2, CheckCircle2, XCircle, ArrowLeft, Gauge } from "lucide-react";
import { getCapabilities, chooseRenderPath } from "@/lib/renderEngine";
import { runWebCodecsPoc } from "@/lib/renderEngine/webcodecsPoc";
import { opfsCache } from "@/lib/renderEngine/opfsCache";
import { exportLocal, canLocalExport } from "@/lib/renderEngine/localExport";

export default function Diagnostics() {
  const nav = useNavigate();
  const [caps, setCaps] = useState(null);
  const [decision, setDecision] = useState(null);
  const [poc, setPoc] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    (async () => {
      setCaps(await getCapabilities(true));
      setDecision(await chooseRenderPath());
    })();
  }, []);

  const runPoc = async () => {
    setRunning(true); setPoc(null);
    const r = await runWebCodecsPoc({ width: 640, height: 360, frames: 60 });
    setPoc(r); setRunning(false);
  };

  const [opfs, setOpfs] = useState(null);
  const runOpfsTest = async () => {
    setOpfs({ busy: true });
    try {
      const payload = new Blob([new Uint8Array(1024 * 64)], { type: "application/octet-stream" });
      await opfsCache.put("diag-selftest.bin", payload);
      const got = await opfsCache.get("diag-selftest.bin");
      const ok = !!got && got.size === payload.size;
      const stats = await opfsCache.stats();
      await opfsCache.delete("diag-selftest.bin");
      setOpfs({ busy: false, ok, stats });
    } catch (e) { setOpfs({ busy: false, ok: false, error: String(e?.message || e) }); }
  };

  const [local, setLocal] = useState(null);
  const runLocalTest = async () => {
    setLocal({ busy: true, progress: 0 });
    try {
      const res = await exportLocal({
        videoUrl: null, layers: null, config: { fontFamily: "Inter" },
        segments: [{ id: 1, start: 0.2, end: 2.6, text: "CaptionMotion local export test",
          words: [{ word: "CaptionMotion", start: 0.2, end: 1.0 }, { word: "local", start: 1.0, end: 1.5 }, { word: "export", start: 1.5, end: 2.0 }, { word: "test", start: 2.0, end: 2.6 }] }],
        width: 640, height: 360, fps: 30, duration: 3,
        onProgress: (p) => setLocal((s) => ({ ...s, progress: p.progress })),
      });
      const url = URL.createObjectURL(res.blob);
      setLocal({ busy: false, ok: true, res, url });
    } catch (e) { setLocal({ busy: false, ok: false, error: String(e?.message || e) }); }
  };

  const Row = ({ label, value }) => (
    <div className="flex items-center justify-between border-b border-white/5 py-2 text-sm">
      <span className="text-[#A1A1AA]">{label}</span>
      {typeof value === "boolean" ? (
        <span className={`flex items-center gap-1 font-semibold ${value ? "text-[#00F0FF]" : "text-[#666]"}`} data-testid={`cap-${label}`}>
          {value ? <CheckCircle2 size={15} /> : <XCircle size={15} />} {value ? "Yes" : "No"}
        </span>
      ) : (
        <span className="font-plex font-semibold tabular-nums text-white" data-testid={`cap-${label}`}>{value ?? "—"}</span>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-4 py-8 text-white" data-testid="diagnostics-page">
      <div className="mx-auto max-w-2xl">
        <button onClick={() => nav(-1)} className="mb-5 flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white"><ArrowLeft size={15} /> Back</button>
        <h1 className="font-display text-3xl font-black tracking-tight">Rendering Engine Diagnostics</h1>
        <p className="mt-1 text-sm text-[#A1A1AA]">Local-first (WebCodecs/WebGPU) rendering is in <b className="text-[#a78bfa]">beta</b>. Exports currently use the optimized server renderer; this page reports your device's readiness for the client-side engine.</p>

        {decision && (
          <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="engine-decision">
            <div className="flex items-center gap-2 text-sm font-bold"><Gauge size={16} className="text-[#00F0FF]" /> Active export path: <span className="text-[#00F0FF]">Server FFmpeg</span></div>
            <div className="mt-1 text-xs text-[#A1A1AA]">Local acceleration: {decision.localAvailable ? <span className="text-[#a78bfa] font-semibold">available (beta)</span> : <span>not available on this device</span>}</div>
            <div className="mt-1 text-xs text-[#666]">{decision.reason}</div>
          </div>
        )}

        {caps && (
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="mb-2 flex items-center gap-2 font-bold"><Cpu size={16} className="text-[#00F0FF]" /> WebCodecs & GPU</div>
              <Row label="WebCodecs" value={caps.webCodecs} />
              <Row label="VideoDecoder" value={caps.videoDecoder} />
              <Row label="VideoEncoder" value={caps.videoEncoder} />
              <Row label="Hardware encoding" value={caps.hardwareEncoding} />
              <Row label="WebGPU" value={caps.webGPU} />
              <Row label="WebGL2" value={caps.webGL2} />
              <Row label="OffscreenCanvas" value={caps.offscreenCanvas} />
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="mb-2 flex items-center gap-2 font-bold"><HardDrive size={16} className="text-[#a78bfa]" /> Storage & Compute</div>
              <Row label="OPFS" value={caps.opfs} />
              <Row label="File System Access" value={caps.fileSystemAccess} />
              <Row label="SharedArrayBuffer" value={caps.sharedArrayBuffer} />
              <Row label="Cross-origin isolated" value={caps.crossOriginIsolated} />
              <Row label="WASM SIMD" value={caps.wasmSimd} />
              <Row label="CPU threads" value={caps.hardwareConcurrency} />
              <Row label="Device memory (GB)" value={caps.deviceMemoryGB} />
            </div>
          </div>
        )}

        {caps && (
          <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="mb-2 text-xs font-bold uppercase tracking-widest text-[#A1A1AA]">Supported codecs</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(caps.codecs || {}).map(([k, v]) => (
                <span key={k} data-testid={`codec-${k}`} className={`rounded-full border px-3 py-1 text-xs font-semibold ${v.supported ? "border-[#00F0FF]/40 text-[#00F0FF]" : "border-white/10 text-[#555]"}`}>
                  {k.toUpperCase()} {v.supported ? (v.hardware ? "· HW" : "· SW") : "· n/a"}
                </span>
              ))}
              {(!caps.codecs || !Object.keys(caps.codecs).length) && <span className="text-xs text-[#666]">No WebCodecs encoder configs supported.</span>}
            </div>
          </div>
        )}

        <div className="mt-6">
          <button data-testid="run-webcodecs-poc" onClick={runPoc} disabled={running || !caps?.webCodecs}
            className="flex items-center gap-2 rounded-full bg-[#00F0FF] px-5 py-3 text-sm font-bold text-black transition-colors hover:bg-[#00d4e0] disabled:opacity-50">
            {running ? <><Loader2 size={16} className="animate-spin" /> Running WebCodecs test…</> : <><Play size={16} /> Test local acceleration (WebCodecs)</>}
          </button>
          {!caps?.webCodecs && <span className="ml-3 text-xs text-[#666]">WebCodecs isn't available in this browser.</span>}
        </div>

        {poc && (
          <div className={`mt-4 rounded-xl border p-4 ${poc.ok ? "border-[#00F0FF]/30 bg-[#00F0FF]/[0.05]" : "border-red-500/30 bg-red-500/[0.06]"}`} data-testid="poc-result">
            <div className={`flex items-center gap-2 font-bold ${poc.ok ? "text-[#00F0FF]" : "text-red-300"}`}>
              {poc.ok ? <CheckCircle2 size={17} /> : <XCircle size={17} />} {poc.ok ? "WebCodecs round-trip succeeded" : "WebCodecs test failed"}
            </div>
            <div className="mt-1 text-xs text-[#A1A1AA]">{poc.reason}</div>
            {poc.ok && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                <Metric l="Codec" v={`${poc.codec?.toUpperCase()} ${poc.hardware ? "(HW)" : "(SW)"}`} />
                <Metric l="Encode" v={`${poc.encodeFps} fps`} />
                <Metric l="Decode" v={`${poc.decodeFps} fps`} />
                <Metric l="Realtime" v={`${poc.realtimeFactor}×`} />
              </div>
            )}
          </div>
        )}
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold"><HardDrive size={15} className="text-[#a78bfa]" /> OPFS cache self-test</div>
            <button data-testid="run-opfs-test" onClick={runOpfsTest} disabled={opfs?.busy || !caps?.opfs}
              className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-white hover:border-white/30 disabled:opacity-50">
              {opfs?.busy ? "Testing…" : "Write + read a 64KB blob"}
            </button>
            {!caps?.opfs && <div className="mt-2 text-[11px] text-[#666]">OPFS unavailable in this browser.</div>}
            {opfs && !opfs.busy && (
              <div className="mt-2 text-xs" data-testid="opfs-result">
                {opfs.ok ? <span className="text-[#00F0FF]">✓ Round-trip OK · {opfs.stats?.entries} entries · {(opfs.stats?.bytes / 1024).toFixed(0)}KB cached</span> : <span className="text-red-300">✕ {opfs.error || "failed"}</span>}
              </div>
            )}
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold"><Zap size={15} className="text-[#a78bfa]" /> Local export self-test</div>
            <button data-testid="run-local-export-test" onClick={runLocalTest} disabled={local?.busy || !canLocalExport()}
              className="rounded-full border border-[#a78bfa]/40 bg-[#a78bfa]/10 px-4 py-2 text-xs font-bold text-[#c4b5fd] hover:bg-[#a78bfa]/20 disabled:opacity-50">
              {local?.busy ? `Encoding… ${local.progress || 0}%` : "Render 3s synthetic clip on device"}
            </button>
            {local && !local.busy && (
              <div className="mt-2 text-xs" data-testid="local-export-result">
                {local.ok ? (
                  <span className="text-[#00F0FF]">✓ {local.res.codec}{local.res.hardware ? " HW" : ""} · {local.res.frames} frames · {local.res.encodeFps} fps · segs {local.res.segsReused}/{local.res.segsTotal} reused · <a className="underline" href={local.url} download={`local-test.${local.res.ext}`}>download</a></span>
                ) : <span className="text-red-300">✕ {local.error || "failed"}</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ l, v }) {
  return <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-[#A1A1AA]">{l}</div><div className="font-plex text-sm font-semibold tabular-nums text-white">{v}</div></div>;
}
